/**
 * Users list view — UI only; state from useUsersViewModel.
 */

import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Edit, Eye, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { LoadingSpinner } from '@/features/common/components/LoadingSpinner'
import { ConfirmDialog } from '@/features/common/components/ConfirmDialog'
import type { UserSortField } from '../models/users_ui_state'
import { UserStatusBadge } from '../widgets/user_status_badge'
import { useUsersViewModel } from '../viewmodels/use_users_viewmodel'

function formatDate(dateString: string) {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function UsersPageView() {
  const navigate = useNavigate()
  const { state, isLoading, error, isDeleting, actions } = useUsersViewModel()

  const getSortIcon = (field: UserSortField) => {
    if (state.sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 text-gray-400" />
    }
    return state.sortDirection === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-gray-700" />
    ) : (
      <ArrowDown className="w-3 h-3 text-gray-700" />
    )
  }

  if (isLoading) {
    return (
      <UsersLoadingState title="User Management" />
    )
  }

  if (error && state.users.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">User Management</h1>
        </div>
        <UsersErrorCard error={error} />
      </div>
    )
  }

  const { filteredUsers, paginatedUsers, deleteDialog, totalPages, startIndex, endIndex } =
    state

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-800">User Management</h1>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={state.entriesPerPage}
              onChange={e => actions.setPageSize(Number(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="search" className="text-sm text-gray-700">
              Search:
            </label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id="search"
                type="text"
                value={state.searchTerm}
                onChange={e => actions.setSearch(e.target.value)}
                placeholder="Search users..."
                className="pl-8 pr-4 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('id')}
                >
                  <div className="flex items-center gap-1 w-full">#{getSortIcon('id')}</div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('name')}
                >
                  <div className="flex items-center gap-1 w-full">
                    Name
                    {getSortIcon('name')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('email')}
                >
                  <SortHeader label="Email" icon={getSortIcon('email')} />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('phoneNo')}
                >
                  <SortHeader label="Phone No" icon={getSortIcon('phoneNo')} />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('status')}
                >
                  <SortHeader label="Status" icon={getSortIcon('status')} />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('createdAt')}
                >
                  <SortHeader label="Created Date" icon={getSortIcon('createdAt')} />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedUsers.length > 0 ? (
                paginatedUsers.map((user, index) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {state.sortField === 'id' ? user.id : startIndex + index + 1}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {user.name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {user.email}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {user.phoneNo}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <UserStatusBadge status={user.status} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                          className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                          className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => actions.openDeleteDialog(user.id, user.name)}
                          className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {filteredUsers.length > 0 ? startIndex + 1 : 0} to{' '}
            {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} entries
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={actions.goToPreviousPage}
              disabled={state.currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => actions.changePage(page)}
                className={`px-3 py-1 text-sm border rounded ${
                  state.currentPage === page
                    ? 'bg-teal-500 text-white border-teal-500'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
                style={
                  state.currentPage === page
                    ? { backgroundColor: '#019b9a', borderColor: '#019b9a' }
                    : undefined
                }
              >
                {page}
              </button>
            ))}
            <button
              onClick={actions.goToNextPage}
              disabled={state.currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={actions.closeDeleteDialog}
        onConfirm={() => void actions.confirmDelete()}
        title="Delete User"
        message={`Are you sure you want to delete "${deleteDialog.userName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  )
}

function UsersLoadingState({ title }: { title: string }) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-800">{title}</h1>
      </div>
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    </div>
  )
}

function UsersErrorCard({ error }: { error: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <p className="text-red-600 font-medium mb-2">Failed to load users</p>
      <p className="text-sm text-gray-500">{error}</p>
    </div>
  )
}

function SortHeader({ label, icon }: { label: string; icon: ReactNode }) {
  return (
    <div className="flex items-center gap-1 w-full">
      {label}
      {icon}
    </div>
  )
}
